import React from 'react'
import { sanitize } from 'dompurify'
import { colors } from '@resiliatech/birdsong'
import { Resource } from 'Generated/academy-graphql-types'
import {
  GoalCategory,
  UserSurveyPrimaryFunction,
  UserSurveyRole,
} from 'Generated/graphql-types'
import { useAppState } from 'Context/AppContext/AppContext'
import { snakeCaseToSpaces } from 'Utils/formatters'
import {
  ActionElementType,
  confidenceLevels,
  priorityGoals,
} from 'Components/SharedComponents/Surveys/constants'
import {
  Description,
  StepContainer,
  Title,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import { NextActions } from 'Components/SharedComponents/Surveys/NextActions/NextActions'
import { UserActions } from 'Components/SharedComponents/Surveys/UserActions/UserActions'
import {
  ButtonAction,
  CustomAction,
  GoalToCreate,
  HandleGoalsArgs,
  Priorities as PrioritiesType,
} from 'Components/SharedComponents/Surveys/types'
import { GoalActionHeader } from '../NeedsAssessment.styled'
import {
  initialPriorities,
  MIN_NUM_OF_SELECTED_TEMPLATE_GOALS,
  orgLeaderPathRoles,
  priorityCategories,
  NeedsAssessmentWizardStep,
} from '../../constants'
import { ConfidenceLevel, HandleAnyAction } from '../../types'
import {
  sendPriorityCompletedEvent,
  sendStepTransitionEvent,
} from '../../utils'

type PrioritiesProps = {
  onAnyAction: HandleAnyAction
  onSetPriorities: (priorities: PrioritiesType) => void
  onSetRecommendedResources: (resources: Resource[]) => void
  onToggleTemplateGoal: (args: HandleGoalsArgs) => GoalToCreate[]
  priorities: PrioritiesType
  userJourneyLength: number
  userPrimaryFunction: UserSurveyPrimaryFunction | undefined
  userRole?: UserSurveyRole
}

export const Priorities = ({
  onAnyAction,
  onSetPriorities,
  onSetRecommendedResources,
  onToggleTemplateGoal,
  priorities,
  userJourneyLength,
  userPrimaryFunction,
  userRole,
}: PrioritiesProps) => {
  const { firstPriority, secondPriority, thirdPriority } = priorities
  const { nonprofitOrgConfig, userConfig } = useAppState()
  const {
    category: firstCategory,
    challenges: firstPriorityChallenges,
    confidenceLevel: firstPriorityConfidence,
  } = firstPriority

  const {
    category: secondCategory,
    challenges: secondPriorityChallenges,
    confidenceLevel: secondPriorityConfidence,
  } = secondPriority

  const firstCategoryLabel = snakeCaseToSpaces(firstCategory, true)
  const secondCategoryLabel = snakeCaseToSpaces(secondCategory, true)
  const firstPriorityGoals = priorityGoals[firstCategory] || []
  const secondPriorityGoals = priorityGoals[secondCategory] || []
  const selectedGoals = [
    ...firstPriority.selectedGoals,
    ...secondPriority.selectedGoals,
    ...thirdPriority.selectedGoals,
  ]

  // Validation
  const priorityIsNotListed = firstCategory === GoalCategory.None

  const isFirstPriorityComplete =
    !!firstCategory && !!firstPriority.confidenceLevel

  const isSecondPriorityComplete =
    !!secondCategory && !!secondPriority.confidenceLevel

  const hasEnoughSelectedGoals =
    selectedGoals.length >= MIN_NUM_OF_SELECTED_TEMPLATE_GOALS

  const isNextButtonEnabled =
    priorityIsNotListed ||
    (hasEnoughSelectedGoals &&
      isFirstPriorityComplete &&
      isSecondPriorityComplete)

  const isSecondPriorityHidden = !isFirstPriorityComplete || priorityIsNotListed

  // Styles
  const checkboxStyles =
    !hasEnoughSelectedGoals && !isSecondPriorityHidden
      ? { color: colors.feedback.errorDark }
      : {}

  // Elements
  const goalActionHeader = (
    <GoalActionHeader>
      How can we support you with this priority?
    </GoalActionHeader>
  )

  const firstPriorityNestedTopics: CustomAction[] = firstPriorityGoals.map(
    (goal, i) => ({
      callback: () =>
        onSetPriorities({
          ...priorities,
          firstPriority: {
            ...firstPriority,
            selectedGoals: onToggleTemplateGoal({
              existingGoals: firstPriority.selectedGoals,
              goal,
            }),
          },
        }),
      checked: firstPriority.selectedGoals.some(
        (selectedGoal) => selectedGoal.label === goal.label
      ),
      header: i === 0 ? goalActionHeader : null,
      label: goal.label,
      styles: checkboxStyles,
      type: ActionElementType.CHECKBOX,
      value: goal.templateLibraryId,
    })
  )

  const secondPriorityNestedTopics: CustomAction[] = secondPriorityGoals.map(
    (goal, i) => ({
      callback: () =>
        onSetPriorities({
          ...priorities,
          secondPriority: {
            ...secondPriority,
            selectedGoals: onToggleTemplateGoal({
              existingGoals: secondPriority.selectedGoals,
              goal,
            }),
          },
        }),
      checked: secondPriority.selectedGoals.some(
        (selectedGoal) => selectedGoal.label === goal.label
      ),
      header: i === 0 ? goalActionHeader : null,
      isHidden: isSecondPriorityHidden,
      label: goal.label,
      styles: checkboxStyles,
      type: ActionElementType.CHECKBOX,
      value: goal.templateLibraryId,
    })
  )

  const userActions: CustomAction[] = [
    {
      callback: (...args: unknown[]) => {
        onSetPriorities({
          ...initialPriorities,
          firstPriority: {
            category: args[0] as GoalCategory,
            challenges: '',
            confidenceLevel: '',
            selectedGoals: [],
            updateNotes: '',
          },
        })
        onSetRecommendedResources([])
      },
      header: 'First priority',
      label: 'Choose your first priority',
      options: priorityCategories,
      required: true,
      type: ActionElementType.SELECT,
      value: firstCategory,
    },
    ...firstPriorityNestedTopics,
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          firstPriority: {
            ...priorities.firstPriority,
            confidenceLevel: args[0] as ConfidenceLevel,
          },
        }),
      label: `From 1 to 5, how would you rate your organization’s knowledge in ${firstCategoryLabel}?`,
      isHidden: !firstCategory || firstCategory === GoalCategory.None,
      options: confidenceLevels,
      required: true,
      type: ActionElementType.SELECT,
      value: firstPriorityConfidence,
    },
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          firstPriority: {
            ...priorities.firstPriority,
            challenges: sanitize(args[0] as string),
          },
        }),
      label: `What would you say is your organization’s main challenge in ${firstCategoryLabel} (Optional)?`,
      isHidden: !firstCategory || firstCategory === GoalCategory.None,
      type: ActionElementType.TEXT_FIELD,
      value: firstPriorityChallenges,
    },
    {
      callback: (...args: unknown[]) => {
        onSetPriorities({
          ...priorities,
          secondPriority: {
            category: args[0] as GoalCategory,
            challenges: '',
            confidenceLevel: '',
            selectedGoals: [],
            updateNotes: '',
          },
        })
        onSetRecommendedResources([])
      },
      header: 'Second priority',
      isHidden: isSecondPriorityHidden,
      label: 'Choose your second priority',
      options: priorityCategories.filter(
        (p) => p.value !== firstCategory && p.value !== GoalCategory.None
      ),
      required: true,
      type: ActionElementType.SELECT,
      value: secondCategory,
    },
    ...secondPriorityNestedTopics,
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          secondPriority: {
            ...secondPriority,
            confidenceLevel: args[0] as ConfidenceLevel,
          },
        }),
      isHidden: !secondCategory,
      label: `From 1 to 5, how would you rate your organization’s knowledge in ${secondCategoryLabel}?`,
      options: confidenceLevels,
      required: true,
      type: ActionElementType.SELECT,
      value: secondPriorityConfidence,
    },
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          secondPriority: {
            ...secondPriority,
            challenges: sanitize(args[0] as string),
          },
        }),
      isHidden: !secondCategory,
      label: `What would you say is your organization’s main challenge in ${secondCategoryLabel} (Optional)?`,
      type: ActionElementType.TEXT_FIELD,
      value: secondPriorityChallenges,
    },
  ]

  const nextStep = priorityIsNotListed
    ? NeedsAssessmentWizardStep.WHAT_ARE_WE_MISSING
    : NeedsAssessmentWizardStep.WE_HAVE_CAPTURED_YOUR_PRIORITIES

  const userId = userConfig.id
  const userEmail = userConfig.email
  const { funderOrgId } = nonprofitOrgConfig
  const nonprofitOrgId = nonprofitOrgConfig.orgId
  const nextActions: ButtonAction[] = [
    {
      disabled: !isNextButtonEnabled,
      label: 'Next',
      path: nextStep,
      saveUserState: true,
      type: ActionElementType.BUTTON,
      callback: () => {
        sendPriorityCompletedEvent({
          nonprofitOrgId: nonprofitOrgConfig.orgId,
          priority: {
            rank: 1,
            category: firstCategory,
            confidenceLevel: firstPriorityConfidence.toString(),
            challenges: firstPriorityChallenges,
            selectedGoals: firstPriority.selectedGoals,
          },
          userEmail,
          userId,
          userPrimaryFunction,
          userRole,
        })

        // Send analytics event for the second priority
        if (secondCategory && secondPriority.confidenceLevel) {
          sendPriorityCompletedEvent({
            nonprofitOrgId,
            priority: {
              rank: 2,
              category: secondCategory,
              confidenceLevel: secondPriority.confidenceLevel,
              challenges: secondPriorityChallenges,
              selectedGoals: secondPriority.selectedGoals,
            },
            userEmail,
            userId,
            userPrimaryFunction,
            userRole,
          })
        }
        sendStepTransitionEvent({
          currentStep: NeedsAssessmentWizardStep.SELECT_YOUR_PRIORITIES,
          nextStep,
          userId,
          nonprofitOrgId,
          funderOrgId,
          userEmail,
          userPrimaryFunction,
          userJourneyLength,
          userRole,
        })
      },
    },
  ]

  const isOrgLeader = userRole && orgLeaderPathRoles.includes(userRole)

  return (
    <StepContainer>
      <Title>
        Select your {isOrgLeader ? "organization's" : 'personal'} priorities
      </Title>
      <Description>
        {isOrgLeader ? 'Set' : 'Your Org Leader set'} org-wide priorities to
        inform our content and resource recommendations, but we also want your
        input. We require two to get started.
      </Description>
      <UserActions onAnyAction={onAnyAction} userActions={userActions} />
      <NextActions nextActions={nextActions} onAnyAction={onAnyAction} />
    </StepContainer>
  )
}
